import { Box, Button, FormControlLabel, Switch } from "@mui/material";
import { useGetOne } from "@mb-pro-ui/utils";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "wouter";
import { related, type Cdec, type Customer } from "../api/types";
import { useCustomerEventStream } from "../api/useCustomerEventStream";
import ConnectionDot from "../components/ConnectionDot";
import { formatEventTime } from "../components/format";
import { DenseList, Row } from "../components/Rows";
import { FullPageSpinner, Notice } from "../components/States";
import TopBar from "../components/TopBar";
import useStorageState from "../useStorageState";

function eventSecondary(intl: ReturnType<typeof useIntl>, cdec: Cdec) {
  const zone = cdec["zone-number"];
  const place = related(cdec.zone)?.place;
  const userName = related(cdec.user)?.name;
  const parts = [
    zone && [zone, place ?? userName].filter(Boolean).join(" "),
    cdec["partition-number"] &&
      intl.formatMessage({ defaultMessage: "partition {number}" }, { number: cdec["partition-number"] }),
    cdec.active === false && intl.formatMessage({ defaultMessage: "inactive" }),
  ];
  return parts.filter(Boolean).join(" · ");
}

export default function EventsPage() {
  const intl = useIntl();
  const { id = "" } = useParams<{ id: string }>();
  const [hideGprsLifesigns, setHideGprsLifesigns] = useStorageState("hide-gprs-lifesigns", true);
  const customer = useGetOne<Customer>("alarm/customers", id, {
    fields: { customers: ["account", "name"] },
  });
  const stream = useCustomerEventStream(id, hideGprsLifesigns);

  return (
    <>
      <TopBar
        back={`/c/${id}`}
        title={
          <>
            <FormattedMessage defaultMessage="Events" />
            {customer.data && ` · ${customer.data.account ?? ""} ${customer.data.name ?? ""}`}
          </>
        }
        end={<ConnectionDot status={stream.status} lastSuccess={stream.lastSuccess} />}
      />
      <Box sx={{ px: 1.5, bgcolor: "background.paper", borderBottom: 1, borderColor: "divider" }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={hideGprsLifesigns}
              onChange={(e) => setHideGprsLifesigns(e.target.checked)}
            />
          }
          label={<FormattedMessage defaultMessage="Hide GPRS lifesigns" />}
          slotProps={{ typography: { variant: "body2" } }}
        />
      </Box>

      {stream.status === "loading" && stream.rows.length === 0 ? (
        <FullPageSpinner />
      ) : stream.rows.length === 0 ? (
        <Notice>
          {stream.status === "error" ? (
            <FormattedMessage defaultMessage="The events could not be loaded." />
          ) : (
            <FormattedMessage defaultMessage="No events." />
          )}
        </Notice>
      ) : (
        <>
          <DenseList>
            {stream.rows.map((cdec) => (
              <Row
                key={cdec.id}
                accent={cdec.color}
                muted={cdec.active === false}
                id={formatEventTime(intl, cdec.arrived)}
                primary={
                  <>
                    <b>{cdec["event-code"]}</b> {cdec["localized-description"]}
                  </>
                }
                primaryLines={2}
                secondary={eventSecondary(intl, cdec)}
              />
            ))}
          </DenseList>
          {stream.hasOlder && (
            <Box sx={{ p: 1.5, textAlign: "center" }}>
              <Button
                variant="outlined"
                size="small"
                disabled={stream.loadingOlder}
                onClick={() => void stream.loadOlder()}
              >
                <FormattedMessage defaultMessage="Load older" />
              </Button>
            </Box>
          )}
        </>
      )}
    </>
  );
}
